/** @file Routes pointer samples: middle button or Space+left pans, left goes to the active tool. */
import { viewCamera } from '@pose2d/editor-geometry/viewCamera';
import type { Id } from '@pose2d/format/types';
import { isHeld } from '@pose2d/shortcuts/isHeld';
import { playhead } from '../../playback/playhead';
import { cameraStore } from '../../stores/cameraStore';
import { playheadStore } from '../../stores/playheadStore';
import { toolStore } from '../../stores/toolStore';
import type { PendingCommand } from '../../stores/types';
import { cameraOf } from './cameraOf';
import { drawFrame } from './drawFrame';
import type { RuntimeState } from './runtimeState';
import type { PreviewValues } from './scene/channelOffset';
import { stopMixPreview } from './stopMixPreview';
import { toolContextOf } from './toolContextOf';
import { toolPointers } from './toolPointers';
import type { PointerKind, ToolGesture, ViewportPointer } from './viewportTypes';
import { viewportStore } from './viewportStore';

/** The active tool's hover hook (W3-34): a move with no pan and no gesture, or `null` for a cancel with none. */
function hover(s: RuntimeState, p: ViewportPointer | null): void {
  const pointer = toolPointers[toolStore.getState().active];
  if (pointer.hover === undefined) return;
  const ctx = toolContextOf(s, cameraOf(s));
  if (ctx === null) return;
  pointer.hover(ctx, p);
  s.dirty = true;
}

/** A tool press edits a still pose (CB-05 B16): playback pauses on the whole frame keys land on and a mix preview
 *  stops (R-E1-04); either one redraws at once, so the pose and the slot geometry the press hit-tests agree. */
function holdStill(s: RuntimeState): void {
  const playing = playheadStore.getState().playing;
  if (playing) playhead.pause();
  if (!stopMixPreview() && !playing) return;
  s.dirty = true;
  if (!drawFrame(s)) toolContextOf(s, cameraOf(s))?.repose();
}

/** Starts a pan (middle button, Space+left) or the active tool's gesture (left) on a still pose. */
function press(s: RuntimeState, p: ViewportPointer): void {
  if (s.gesture !== null || s.pan !== null) return;
  if (p.button === 1 || (p.button === 0 && isHeld('Space'))) {
    s.pan = [p.sx, p.sy];
    return;
  }
  if (p.button !== 0 || toolContextOf(s, cameraOf(s)) === null) return;
  holdStill(s);
  const ctx = toolContextOf(s, cameraOf(s));
  if (ctx === null) return;
  s.base = new Map(s.preview);
  s.gesture = toolPointers[ctx.tool.active].press(ctx, p);
  s.dirty = true;
}

/** Pans the camera, moves the gesture, or hovers. */
function move(s: RuntimeState, p: ViewportPointer): void {
  if (s.pan !== null) {
    cameraStore.setState(viewCamera.pan(cameraStore.getState(), p.sx - s.pan[0], p.sy - s.pan[1]));
    s.pan = [p.sx, p.sy];
  } else if (s.gesture !== null) s.gesture.move(p);
  else hover(s, p);
}

/** `bone/channel` pairs a call writes (setKey: its channel, setTransform: its value's channels); `bone/*` when it
 *  names no channel. */
function writtenBy(c: PendingCommand): string[] {
  const input = c.input as { target?: unknown; channel?: unknown; value?: unknown } | null;
  const target = String(input?.target);
  if (typeof input?.channel === 'string') return [`${target}/${input.channel}`];
  const value = input?.value;
  return c.name === 'bone.setTransform' && typeof value === 'object' && value !== null ? Object.keys(value).map((ch) => `${target}/${ch}`) : [`${target}/*`];
}

/** Whether the batch wrote `channel` of bone `target`. */
type Written = (target: string, channel: string) => boolean;

/** The held values of bone `id` the batch did not write, or null when none is left. */
function heldLeft(id: string, values: PreviewValues, written: Written): PreviewValues | null {
  const left = Object.entries(values).filter(([ch]) => !written(id, ch));
  return left.length === 0 ? null : Object.fromEntries(left);
}

/** After a commit the held pose stays drawn and keyable, minus the channels the batch itself just wrote (a bone whose
 *  every held channel was written leaves the held pose). */
function keepHeld(s: RuntimeState, batch: PendingCommand[]): void {
  const keys = new Set(batch.flatMap(writtenBy));
  const written: Written = (target, channel) => keys.has(`${target}/*`) || keys.has(`${target}/${channel}`);
  s.preview = new Map([...s.base].flatMap(([id, values]): [Id, PreviewValues][] => {
    const left = heldLeft(id, values, written);
    return left === null ? [] : [[id, left]];
  }));
  const { unkeyed, notice } = viewportStore.getState();
  const left = unkeyed.filter((c) => {
    const input = c.input as { target?: unknown; channel?: unknown } | null;
    return !written(String(input?.target), String(input?.channel));
  });
  if (left.length === unkeyed.length) return;
  viewportStore.setState({ unkeyed: left, notice: left.length === 0 && notice?.kind === 'offer' ? null : notice });
}

/** Commits (handing the gesture what it created, W3-11), or (Animate, auto-key off, keys only) holds the batch as the
 *  unkeyed pose (R-E1-07). */
function finish(s: RuntimeState, gesture: ToolGesture, batch: PendingCommand[]): void {
  s.gizmo = null;
  s.dirty = true;
  const hold = toolStore.getState().mode === 'animate' && !playheadStore.getState().autoKey && batch.every((c) => c.name === 'animation.setKey');
  if (batch.length > 0 && hold) {
    viewportStore.setState({ unkeyed: [...viewportStore.getState().unkeyed, ...batch], notice: { kind: 'offer', key: 'viewport.unkeyed', action: 'key' } });
    return;
  }
  if (batch.length === 0) {
    s.preview = new Map(s.base);
    return;
  }
  const created = s.deps.commit(batch);
  gesture.committed?.(created);
  keepHeld(s, batch);
}

/** `up` reposes with the preview and releases; `cancel` drops the gesture and restores the held preview. */
function end(s: RuntimeState, gesture: ToolGesture, kind: 'up' | 'cancel', p: ViewportPointer): void {
  s.gesture = null;
  if (kind === 'up') {
    toolContextOf(s, cameraOf(s))?.repose();
    finish(s, gesture, gesture.release(p));
    return;
  }
  gesture.cancel();
  Object.assign(s, { gizmo: null, preview: new Map(s.base), dirty: true });
}

/** One sample of `kind`; `cancel` drops the gesture and restores the held preview. */
export function routePointer(s: RuntimeState, kind: PointerKind, p: ViewportPointer): void {
  if (kind === 'down') press(s, p);
  else if (kind === 'move') move(s, p);
  else if (s.pan !== null) s.pan = null;
  else if (s.gesture !== null) end(s, s.gesture, kind, p);
  else if (kind === 'cancel') hover(s, null);
}
